import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface SessionItem {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  coachEmail?: string | null;
  courtNumber?: number | null;
  sessionType?: string | null;
  attendeeCount: number;
}

interface SessionTableProps {
  sessions: SessionItem[];
  total: number;
  isLoading: boolean;
  onCreateSession: () => void;
}

import { formatDate, formatTime } from '@/lib/format';

export function SessionTable({
  sessions,
  total,
  isLoading,
  onCreateSession,
}: SessionTableProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          All Sessions
          {!isLoading && (
            <Badge variant="secondary" className="ml-2">
              {total} total
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-muted-foreground">No sessions scheduled yet.</p>
            <Button
              variant="outline"
              onClick={onCreateSession}
              className="mt-4"
            >
              Schedule your first session
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div
                key={session.id}
                onClick={() => navigate(`/director/sessions/${session.id}`)}
                className="p-4 border rounded-lg hover:bg-muted/40 transition cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold">
                        {formatDate(session.date).split(' ')[1]}
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {formatDate(session.date)}
                        </p>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground ml-15">
                      {session.coachEmail && (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {session.coachEmail.split('@')[0]}
                        </div>
                      )}
                      {session.courtNumber && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Court {session.courtNumber}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    {session.sessionType && (
                      <Badge variant="outline" className="capitalize">
                        {session.sessionType}
                      </Badge>
                    )}
                    <Badge variant="secondary">
                      {session.attendeeCount} attended
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
